import { normalize } from 'normalizr';
import { ActionTree } from 'vuex';
import { forumMutation } from '@/helpers/graphql';
import { CommentState } from './state';
import RootState from '@/store/root_state';
import {
  createCommentQuery,
  updateCommentScoreById,
  deleteComment,
  createCommentReplyQuery,
  updateComment,
} from '../../queries/forumAPIQueries';
import { comment as commentSchema } from '../forum_api_normalizr_schema';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '@/i18n';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import { FileModel } from '@/models/FileModel';

const actions: ActionTree<CommentState, RootState> = {
  async CREATE_FORUM_API_COMMENT({ commit }, payload) {
    const { discussionId, userId, text, attachments } = payload;

    const response = await forumMutation(createCommentQuery, {
      discussionId,
      userId,
      text,
      attachments,
    });

    const normalizedResponse = normalize(response.data, { createComment: commentSchema });

    commit('SET_FORUM_API_COMMENTS', normalizedResponse.entities);
  },
  async CREATE_FORUM_API_COMMENT_REPLY({ commit }, payload) {
    const { quotedCommentId, userId, text, attachments } = payload;

    const response = await forumMutation(createCommentReplyQuery, {
      quotedCommentId,
      userId,
      text,
      attachments,
    });

    const normalizedResponse = normalize(response.data, { createComment: commentSchema });

    commit('SET_FORUM_API_COMMENTS', normalizedResponse.entities);
    commit('UPDATE_FORUM_API_COMMENT_REPLIES', {
      quotedCommentId,
      entities: normalizedResponse.entities,
    });
  },
  async UPDATE_FORUM_COMMENT_SCORE({ commit }, comment: UnifiedCommentModel) {
    try {
      commit('CHANGE_FORUM_COMMENT_SCORE_UPDATING_STATUS', {
        commentId: comment.id,
        updating: true,
      });

      await forumMutation(updateCommentScoreById, {
        commentId: comment.id,
        score: comment.score,
      });

      commit('SET_UPDATED_FORUM_COMMENT_SCORE', {
        commentId: comment.id,
        score: comment.score,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToUpdateStudentScore').toString(),
      });
    }

    commit('CHANGE_FORUM_COMMENT_SCORE_UPDATING_STATUS', {
      commentId: comment.id,
      updating: false,
    });
  },

  async DELETE_FORUM_COMMENT({ commit }, comment: UnifiedCommentModel) {
    try {
      await forumMutation(deleteComment, {
        commentId: comment.id,
      });

      commit('DELETE_FORUM_COMMENT', {
        commentId: comment.id,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToDeleteComment').toString(),
      });
    }
  },

  async UPDATE_FORUM_COMMENT(
    { commit },
    payload: { text: string; commentId: string; attachments: FileModel[] },
  ) {
    try {
      const { text, attachments, commentId } = payload;
      await forumMutation(updateComment, { text, attachments, commentId });

      commit('UPDATE_FORUM_COMMENT', {
        text,
        attachments,
        commentId,
      });
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToEditComment').toString(),
      });
    }
  },
};

export default actions;
